import React, { useEffect, useState } from "react";
import {
  Button, Card, CardBody, CardHeader, Col, Container, Row,
  Modal, ModalHeader, ModalBody, FormGroup, Label, Input,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "../../components/Pagination/Pagination";
import {
  confirmDelete,
  createOrUpdateProjectCategory,
  getProjectCategories as fetchProjectCategories,
  fetchProjectCategoryForEdit,
} from "./ProjectCategoryService";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { ConfirmDialog } from "primereact/confirmdialog";

const ProjectCategoryListTables = () => {
  document.title = "Project Categories | Valitan IMS";

  const [projectCategories, setProjectCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const [projectCategoryName, setProjectCategoryName] = useState("");
  const [projectCategoryDescription, setProjectCategoryDescription] = useState("");
  const [projectCategoryCode, setProjectCategoryCode] = useState("");

  const [errors, setErrors] = useState({
    projectCategoryName: "",
    projectCategoryDescription: "",
    projectCategoryCode: "",
  });

  const [editingProjectCategoryId, setEditingProjectCategoryId] = useState(null);

  const [paginationInfo, setPaginationInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0,
    pageSize: 10,
    sort: {},
  });

  useEffect(() => {
    loadProjectCategories(0, paginationInfo.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationInfo.pageSize]);

  const loadProjectCategories = async (page = 0, size = 10) => {
    const { projectCategories, paginationInfo } = await fetchProjectCategories(page, size);
    setProjectCategories(projectCategories || []);
    setPaginationInfo(paginationInfo || {
      totalElements: 0, totalPages: 0, pageNumber: 0, pageSize: size, sort: {},
    });
  };

  const handleNextPage = () => {
    if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
      loadProjectCategories(paginationInfo.pageNumber + 1, paginationInfo.pageSize);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.pageNumber > 0) {
      loadProjectCategories(paginationInfo.pageNumber - 1, paginationInfo.pageSize);
    }
  };

  const openModalForEdit = async (id) => {
    setEditingProjectCategoryId(id);
    try {
      const field = await fetchProjectCategoryForEdit(id);
      if (field) {
        setProjectCategoryName(field.name ?? "");
        setProjectCategoryDescription(field.description ?? "");
        setProjectCategoryCode(field.code ?? "");
        setModalOpen(true);
      } else {
        console.error("Project category data is undefined");
      }
    } catch (error) {
      console.error("Error fetching project category data:", error);
    }
  };

  const toggleModal = () => {
    setModalOpen((prev) => !prev);
    // When opening modal for "Add", reset fields
    if (!modalOpen) {
      setProjectCategoryName("");
      setProjectCategoryDescription("");
      setProjectCategoryCode("");
      setErrors({
        projectCategoryName: "",
        projectCategoryDescription: "",
        projectCategoryCode: "",
      });
      setEditingProjectCategoryId(null);
    }
  };

  const startIndex = paginationInfo.pageNumber * paginationInfo.pageSize + 1;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Project categories management" breadcrumbItem="Project categories" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <Button
                      color="primary"
                      className="add-btn float-end"
                      id="TooltipTop"
                      onClick={toggleModal}
                    >
                      <i className="ri-add-line align-bottom me-1"></i> Add
                    </Button>
                  </h4>
                </CardHeader>

                <CardBody>
                  <div>
                    <Row className="g-4 mb-3">
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                          <div className="search-box ms-2">
                            <input type="text" className="form-control search" placeholder="Search..." />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <table className="table align-middle table-nowrap" id="customerTable">
                        <thead className="table-light">
                          <tr>
                            <th className="sort" data-sort="sn">#</th>
                            <th className="sort" data-sort="project-category">Name</th>
                            <th className="sort" data-sort="project-code">Code</th>
                            <th className="sort" data-sort="project-description">Description</th>
                            <th className="sort" data-sort="action">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectCategories.length > 0 ? (
                            projectCategories.map((projectCategory, index) => (
                              <tr key={projectCategory.id}>
                                <td>{startIndex + index}</td>
                                <td>{projectCategory.name}</td>
                                <td>{projectCategory.code}</td>
                                <td>{projectCategory.description}</td>
                                <td>
                                  <div className="btn-group" role="group" aria-label="Basic example">
                                    <button
                                      id="TooltipTop"
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      onClick={() => openModalForEdit(projectCategory.id)}
                                    >
                                      <i className="ri-edit-box-line"></i>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => confirmDelete(projectCategory.id, loadProjectCategories)}
                                      className="btn btn-danger btn-sm"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" style={{ textAlign: "center" }}>
                                No records found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <Pagination
                      paginationInfo={paginationInfo}
                      handleNextPage={handleNextPage}
                      handlePreviousPage={handlePreviousPage}
                      onPageChange={(pageNumber, pageSize) => loadProjectCategories(pageNumber, pageSize)}
                      onPageSizeChange={(newPageSize) => {
                        setPaginationInfo((prev) => ({
                          ...prev,
                          pageSize: newPageSize,
                          pageNumber: 0,
                        }));
                      }}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ConfirmDialog */}
      <ConfirmDialog />

      {/* Toasts */}
      <ToastContainer />

      <Modal isOpen={modalOpen} toggle={toggleModal} backdrop={"static"}>
        <ModalHeader toggle={toggleModal}>
          {editingProjectCategoryId ? "Edit Project Category" : "Add Project Category"}
        </ModalHeader>
        <ModalBody>
          <form
            onSubmit={(evt) =>
              createOrUpdateProjectCategory(
                evt,
                projectCategoryName,
                projectCategoryDescription,
                projectCategoryCode,
                editingProjectCategoryId,
                loadProjectCategories,
                toggleModal,
                setErrors
              )
            }
          >
            <FormGroup>
              <Label for="projectCategoryName">Name</Label>
              <Input
                type="text"
                id="projectCategoryName"
                placeholder="Enter Name"
                value={projectCategoryName}
                onChange={(e) => setProjectCategoryName(e.target.value)}
                invalid={!!errors.projectCategoryName}
              />
              {errors.projectCategoryName && (
                <div className="invalid-feedback">{errors.projectCategoryName}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="projectCategoryCode">Code</Label>
              <Input
                type="text"
                id="projectCategoryCode"
                value={projectCategoryCode}
                onChange={(e) => setProjectCategoryCode(e.target.value)}
                invalid={!!errors.projectCategoryCode}
              />
              {errors.projectCategoryCode && (
                <div className="invalid-feedback">{errors.projectCategoryCode}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="projectCategoryDescription">Description</Label>
              <Input
                type="textarea"
                id="projectCategoryDescription"
                placeholder="Enter Description"
                value={projectCategoryDescription}
                onChange={(e) => setProjectCategoryDescription(e.target.value)}
                invalid={!!errors.projectCategoryDescription}
                rows={4}
              />
              {errors.projectCategoryDescription && (
                <div className="invalid-feedback">{errors.projectCategoryDescription}</div>
              )}
            </FormGroup>

            <Button type="submit" color="primary" className="float-end">
              {editingProjectCategoryId ? "Update" : "Save"}
            </Button>
          </form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ProjectCategoryListTables;
